import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ArrowRight, Lock, Scissors, Search } from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import api from '@/lib/api';
import { usePode } from '@/stores/authStore';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import { nomeDaRodada } from './rodadas';
import Preloader from '@/components/Preloader';
import LogoFornecedor from '@/components/LogoFornecedor';
import PassosDaCotacao from './PassosDaCotacao';
import SeletorDeRodada from './SeletorDeRodada';
import type { ResumoFechamento, Situacao } from './situacao';

type Celula = {
  fornecedor_id: number;
  fornecedor: string;
  preco_metro: number | null;
  preco_efetivo: number | null;
  origem: string;
  nao_cotou: boolean;
  excluido: boolean;
  vencedor: boolean;
  acima_do_melhor: number;
};

type Linha = {
  item_id: number;
  ordem: number;
  produto: string;
  embalagem: string | null;
  quantidade_m: number;
  cores: Record<string, number>;
  melhor_preco: number | null;
  precos: Celula[];
};

type FornecedorResumo = {
  id: number;
  apelido: string;
  logo_url: string | null;
  excluido: boolean;
  pacote: number;
  acima_do_melhor: number;
};

type Equalizacao = {
  rodada: { id: number; numero: number; descricao: string; data: string | null };
  linhas: Linha[];
  melhor_pacote: number;
  quantidade_total_m: number;
  fornecedores: FornecedorResumo[];
};

type Cotacao = {
  id: number;
  numero: number;
  descricao: string;
  status: string;
  origem: { id: number; numero: number } | null;
  separadas: { id: number; numero: number; itens: string[] }[];
  fechamento: ResumoFechamento | null;
  rodadas: { id: number; numero: number; descricao: string; data: string | null; propostas: unknown[] }[];
  situacao: Situacao;
};

/** As cores da cotação são as cores da isolação do cabo — dá para mostrá-las. */
const CORES: Record<string, string> = {
  Am: '#eab308', Az: '#2563eb', Bc: '#ffffff', Pt: '#1e293b', Vd: '#16a34a',
  Vm: '#dc2626', Cz: '#94a3b8', LJ: '#ea580c', LL: '#38bdf8', Mr: '#78350f',
  Cristal: '#e0f2fe', Nú: '#b87333', 'Vd/Am': '#84cc16', 'pt/vm': '#7f1d1d',
};

const reais = new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const preco = new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 4, maximumFractionDigits: 4 });
const inteiro = new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 0 });
/**
 * Cabeçalho e rodapé ficam fixos enquanto a tabela rola. Célula fixa perde a
 * borda da tabela ao rolar, então a divisória vem de uma sombra interna.
 */
const CABECALHO_FIXO = 'bg-surface-subtle px-sm py-xs font-bold shadow-[inset_-1px_-1px_0_var(--color-border)]';
const RODAPE_FIXO = 'bg-surface-subtle px-sm py-xs align-top shadow-[inset_-1px_1px_0_var(--color-border)]';

const percentual = (v: number) => `${new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v * 100)}%`;

function Equalizacao() {
  const { cotacaoId } = useParams();
  const [parametros] = useSearchParams();
  // A rodada vem do passo a passo (?rodada=): mudar de tela não troca de rodada.
  const [rodadaId, setRodadaId] = useState<number | null>(
    parametros.get('rodada') ? Number(parametros.get('rodada')) : null,
  );
  const [excluidos, setExcluidos] = useState<number[]>([]);
  const [busca, setBusca] = useState('');
  const navigate = useNavigate();
  const podeSeparar = usePode('cotacao.equalizar');
  const podeFechar = usePode('cotacao.fechar');
  // Itens marcados para ir a uma cotação nova, negociada e fechada à parte.
  const [marcados, setMarcados] = useState<number[]>([]);
  const [confirmandoSeparacao, setConfirmandoSeparacao] = useState(false);

  const { data: cotacao } = useQuery({
    queryKey: ['cotacao', cotacaoId],
    queryFn: async () => (await api.get<{ data: Cotacao }>(`/cotacoes/${cotacaoId}`)).data.data,
  });

  const rodadaAtual = rodadaId ?? cotacao?.rodadas[cotacao.rodadas.length - 1]?.id ?? null;

  const queryClient = useQueryClient();
  const { data: equalizacao, isLoading, dataUpdatedAt } = useQuery({
    queryKey: ['equalizacao', rodadaAtual, excluidos],
    enabled: rodadaAtual !== null,
    queryFn: async () => {
      const params = new URLSearchParams();
      excluidos.forEach((id) => params.append('excluir[]', String(id)));

      return (
        await api.get<{ data: Equalizacao }>(`/rodadas/${rodadaAtual}/equalizacao?${params.toString()}`)
      ).data.data;
    },
  });

  // Abrir a equalização fica registrado no servidor (é o que libera a próxima
  // rodada); a situação da cotação é relida para o passo a passo acompanhar.
  useEffect(() => {
    if (dataUpdatedAt) void queryClient.invalidateQueries({ queryKey: ['cotacao', cotacaoId] });
  }, [dataUpdatedAt, cotacaoId, queryClient]);

  const linhas = useMemo(() => {
    const termo = busca.trim().toLowerCase();
    if (!equalizacao || !termo) return equalizacao?.linhas ?? [];

    return equalizacao.linhas.filter((linha) =>
      `${linha.produto} ${linha.embalagem ?? ''}`.toLowerCase().includes(termo),
    );
  }, [equalizacao, busca]);

  const alternarFornecedor = (id: number) =>
    setExcluidos((atual) => (atual.includes(id) ? atual.filter((i) => i !== id) : [...atual, id]));

  const alternarItem = (id: number) =>
    setMarcados((atual) => (atual.includes(id) ? atual.filter((i) => i !== id) : [...atual, id]));

  const separar = useMutation({
    mutationFn: async () =>
      (await api.post<{ data: { id: number; numero: number } }>(`/cotacoes/${cotacaoId}/separacoes`, { itens: marcados })).data.data,
    onSuccess: (nova) => {
      setMarcados([]);
      setConfirmandoSeparacao(false);
      // Navega na hora: esperar as consultas refeitas (com o banco remoto) segurava
      // o comprador uns 18 s na tela antiga. Elas se atualizam em segundo plano.
      navigate(`/cotacoes/${nova.id}/equalizacao`);
      void queryClient.invalidateQueries({ queryKey: ['cotacoes'] });
      void queryClient.invalidateQueries({ queryKey: ['cotacao', cotacaoId] });
      void queryClient.invalidateQueries({ queryKey: ['equalizacao'] });
    },
  });

  const erroDaSeparacao = separar.error
    ? ((separar.error as { response?: { data?: { errors?: Record<string, string[]>; message?: string } } }).response?.data?.errors?.itens?.[0] ??
      'Não foi possível separar os itens.')
    : null;

  if (!cotacao) {
    return <Preloader telaCheia mensagem="Carregando cotação…" className="py-3xl" />;
  }

  const ativos = equalizacao?.fornecedores.filter((f) => !f.excluido) ?? [];
  const menorPacote = ativos.length > 0 ? Math.min(...ativos.map((f) => f.pacote)) : 0;
  const fechada = cotacao.status === 'fechada';
  const separavel = podeSeparar && !fechada;
  const valorMarcado = (equalizacao?.linhas ?? [])
    .filter((linha) => marcados.includes(linha.item_id))
    .reduce((soma, linha) => soma + (linha.melhor_preco ?? 0) * linha.quantidade_m, 0);

  return (
    <div className="px-lg py-lg">
      <PassosDaCotacao cotacao={cotacao} atual="equalizacao" rodadaId={rodadaAtual} />

      <div className="mb-md flex flex-wrap items-start justify-between gap-md">
        <div className="min-w-0">
          <p className="text-[12px] font-bold tracking-wide text-accent uppercase">
            Equalização · {nomeDaRodada(cotacao.rodadas.find((rodada) => rodada.id === rodadaAtual)?.numero ?? 1)}
          </p>
          <h1 className="text-[18px] font-bold text-ink">
            <span className="mr-sm text-accent tabular-nums">Nº {cotacao.numero}</span>
            {cotacao.descricao}
          </h1>
          <p className="text-[13px] text-muted">
            {equalizacao
              ? `${equalizacao.linhas.length} linhas · ${inteiro.format(equalizacao.quantidade_total_m)} metros`
              : ''}
          </p>
        </div>

        {rodadaAtual !== null && (
          <Link
            to={`/cotacoes/${cotacao.id}/analise?rodada=${rodadaAtual}${excluidos.length ? `&excluir=${excluidos.join(',')}` : ''}`}
            className="inline-flex shrink-0 items-center gap-xs rounded-md bg-accent px-md py-sm text-[13px] font-bold text-white hover:bg-accent/90"
          >
            Próximo: análise dos resultados
            <ArrowRight size={15} />
          </Link>
        )}
      </div>

      <div className="mb-md">
        <SeletorDeRodada rodadas={cotacao.rodadas} atual={rodadaAtual} aoEscolher={setRodadaId} />
      </div>

      {(cotacao.origem || cotacao.separadas.length > 0 || cotacao.fechamento) && (
        <div className="mb-md space-y-xs rounded-lg border border-border bg-surface px-md py-sm text-[13px] text-ink-soft">
          {cotacao.fechamento && (
            <p className="flex flex-wrap items-center gap-x-xs">
              <Lock size={13} aria-hidden="true" />
              Pacote fechado com a <b className="text-ink">{cotacao.fechamento.fornecedor}</b>.
              <Link to={`/cotacoes/${cotacao.id}/fechamento`} className="font-bold text-accent hover:underline">
                Ver fechamento
              </Link>
            </p>
          )}
          {cotacao.origem && (
            <p>
              Separada da{' '}
              <Link to={`/cotacoes/${cotacao.origem.id}/equalizacao`} className="font-bold text-accent hover:underline">
                Nº {cotacao.origem.numero}
              </Link>
              : começou com os preços da última rodada dela.
            </p>
          )}
          {cotacao.separadas.map((separada) => (
            <p key={separada.id}>
              {separada.itens.length === 1 ? '1 item separado' : `${separada.itens.length} itens separados`} para a{' '}
              <Link to={`/cotacoes/${separada.id}/equalizacao`} className="font-bold text-accent hover:underline">
                Nº {separada.numero}
              </Link>
              <span className="text-muted"> — {separada.itens.join(', ')}</span>
            </p>
          ))}
        </div>
      )}

      <div className="grid gap-md sm:grid-cols-2 lg:grid-cols-4 mb-md">
        {equalizacao?.fornecedores.map((fornecedor) => {
          const vence = !fornecedor.excluido && fornecedor.pacote === menorPacote;

          return (
            <div
              key={fornecedor.id}
              className={`rounded-lg border p-md ${
                vence ? 'border-success bg-success/5' : 'border-border bg-surface'
              } ${fornecedor.excluido ? 'opacity-50' : ''}`}
            >
              <div className="flex items-center justify-between gap-sm">
                <span className="inline-flex items-center gap-xs text-[13px] font-bold text-ink">
                  <LogoFornecedor logoUrl={fornecedor.logo_url} apelido={fornecedor.apelido} tamanho="sm" />
                  {fornecedor.apelido}
                </span>
                <label className="flex items-center gap-xs text-[11px] text-muted">
                  <input
                    type="checkbox"
                    checked={fornecedor.excluido}
                    onChange={() => alternarFornecedor(fornecedor.id)}
                  />
                  tirar da análise
                </label>
              </div>
              <p className="mt-xs text-[19px] font-bold tabular-nums text-ink">
                R$ {reais.format(fornecedor.pacote)}
              </p>
              <p className="text-[12px] text-muted">
                {fornecedor.excluido
                  ? 'não disputa o melhor preço'
                  : `${percentual(-fornecedor.acima_do_melhor)} acima do melhor pacote`}
              </p>
              {podeFechar && !fechada && !fornecedor.excluido && (
                <Link
                  to={`/cotacoes/${cotacao.id}/fechamento?fornecedor=${fornecedor.id}`}
                  className="mt-sm inline-flex text-[12px] font-bold text-accent hover:underline"
                >
                  Fechar o pacote com a {fornecedor.apelido}
                </Link>
              )}
            </div>
          );
        })}

        {equalizacao && (
          <div className="rounded-lg border border-accent/40 bg-accent/5 p-md">
            <span className="text-[13px] font-bold text-ink">Melhor pacote</span>
            <p className="mt-xs text-[19px] font-bold tabular-nums text-accent">
              R$ {reais.format(equalizacao.melhor_pacote)}
            </p>
            <p className="text-[12px] text-muted">o menor preço de cada item, somado</p>
          </div>
        )}
      </div>

      <div className="rounded-lg border border-border bg-surface">
        <div className="flex flex-wrap items-center gap-sm border-b border-border px-md py-sm">
          {/* Largura em px: com a escala de espaçamento do projeto (xs = 4px),
              "max-w-xs" vira 4px e o campo some. */}
          <label className="relative block w-full max-w-[320px]">
            <Search
              size={14}
              className="pointer-events-none absolute top-1/2 left-sm -translate-y-1/2 text-muted"
              aria-hidden="true"
            />
            <input
              type="search"
              value={busca}
              onChange={(evento) => setBusca(evento.target.value)}
              placeholder="Buscar produto"
              aria-label="Buscar produto"
              className="w-full rounded-md border border-border bg-surface py-xs pr-sm pl-[28px] text-[13px] text-ink placeholder:text-muted focus:border-accent focus:ring-2 focus:ring-accent/20 focus:outline-none"
            />
          </label>
          {busca.trim() !== '' && (
            <span className="text-[12px] text-muted">
              {linhas.length} de {equalizacao?.linhas.length ?? 0} linhas
            </span>
          )}
          {separavel && marcados.length > 0 && (
            <div className="ml-auto flex flex-wrap items-center gap-sm">
              <span className="text-[12px] text-ink-soft">
                <b className="tabular-nums">{marcados.length}</b> {marcados.length === 1 ? 'item marcado' : 'itens marcados'} ·{' '}
                <span className="tabular-nums">R$ {reais.format(valorMarcado)}</span> no melhor pacote
              </span>
              <button type="button" onClick={() => setMarcados([])} className="text-[12px] text-muted hover:text-ink">
                Limpar
              </button>
              <button
                type="button"
                onClick={() => setConfirmandoSeparacao(true)}
                className="inline-flex items-center gap-xs rounded-md bg-accent px-md py-xs text-[12px] font-bold text-white hover:bg-accent/90"
              >
                <Scissors size={13} aria-hidden="true" />
                Separar em nova cotação
              </button>
            </div>
          )}
        </div>

        {/* A tabela rola com a própria página. O cabeçalho gruda logo abaixo da
            barra do topo e o total no pé da tela — o "congelar painéis" da
            planilha do comprador: descendo até a linha 40, ele continua sabendo
            de quem é cada coluna. Grudar exige que a moldura não role de lado:
            em tela estreita ela rola, e o cabeçalho e o total não grudam —
            grudariam dentro dela, o cabeçalho deslocado por cima das linhas. */}
        <div className="overflow-x-auto xl:overflow-visible">
          {isLoading || !equalizacao ? (
            <Preloader telaCheia mensagem="Calculando a equalização…" />
          ) : (
            <table className="w-full text-[13px]">
              <thead className="z-20 text-[11px] uppercase tracking-wide text-ink-soft xl:sticky xl:top-[var(--altura-barra,54px)]">
                <tr>
                  <th rowSpan={2} className={`${CABECALHO_FIXO} sticky left-0 z-10 min-w-[230px] text-left`}>
                    Produto
                  </th>
                  <th rowSpan={2} className={`${CABECALHO_FIXO} text-right`}>
                    Quantidade
                  </th>
                  <th colSpan={equalizacao.fornecedores.length} className={`${CABECALHO_FIXO} text-center`}>
                    Preço por metro
                  </th>
                  <th colSpan={equalizacao.fornecedores.length + 1} className={`${CABECALHO_FIXO} text-center`}>
                    Valor no pacote (R$)
                  </th>
                </tr>
                <tr>
                  {equalizacao.fornecedores.map((f) => (
                    <th key={`preco-${f.id}`} className={`${CABECALHO_FIXO} text-right ${f.excluido ? 'opacity-50' : ''}`}>
                      <span className="inline-flex items-center justify-end gap-xs">
                        <LogoFornecedor logoUrl={f.logo_url} apelido={f.apelido} tamanho="sm" />
                        {f.apelido}
                      </span>
                    </th>
                  ))}
                  {equalizacao.fornecedores.map((f) => (
                    <th key={`pacote-${f.id}`} className={`${CABECALHO_FIXO} text-right ${f.excluido ? 'opacity-50' : ''}`}>
                      <span className="inline-flex items-center justify-end gap-xs">
                        <LogoFornecedor logoUrl={f.logo_url} apelido={f.apelido} tamanho="sm" />
                        {f.apelido}
                      </span>
                    </th>
                  ))}
                  <th className={`${CABECALHO_FIXO} text-right text-accent`}>Melhor pacote</th>
                </tr>
              </thead>
              <tbody>
                {linhas.map((linha) => (
                  <tr key={linha.item_id} className="border-b border-border bg-surface even:bg-dominant hover:bg-surface-subtle">
                    <td className="sticky left-0 z-10 border-r border-border bg-inherit px-sm py-xs">
                      {separavel ? (
                        <label className="flex cursor-pointer items-start gap-xs">
                          <input
                            type="checkbox"
                            checked={marcados.includes(linha.item_id)}
                            onChange={() => alternarItem(linha.item_id)}
                            aria-label={`Marcar ${linha.produto} para separar`}
                            className="mt-[3px]"
                          />
                          <span className="block font-medium text-ink">{linha.produto}</span>
                        </label>
                      ) : (
                        <span className="block font-medium text-ink">{linha.produto}</span>
                      )}
                      <span className="text-[11px] text-muted">{linha.embalagem}</span>
                      <span className="mt-xs flex h-1 w-36 overflow-hidden rounded-sm bg-dominant" aria-hidden="true">
                        {Object.entries(linha.cores).map(([cor, quantidade]) => (
                          <i
                            key={cor}
                            className="block h-full"
                            style={{
                              width: `${(quantidade / linha.quantidade_m) * 100}%`,
                              background: CORES[cor] ?? '#cbd5e1',
                            }}
                          />
                        ))}
                      </span>
                    </td>
                    <td className="border-r border-border px-sm py-xs text-right tabular-nums">
                      {inteiro.format(linha.quantidade_m)} m
                    </td>
                    {linha.precos.map((celula) => (
                      <td
                        key={`preco-${celula.fornecedor_id}`}
                        className={`border-r border-border px-sm py-xs text-right tabular-nums ${
                          celula.vencedor ? 'bg-success/10' : ''
                        } ${celula.excluido ? 'opacity-40' : ''}`}
                      >
                        <span className={`block font-medium ${celula.nao_cotou ? 'text-danger' : celula.vencedor ? 'text-success' : 'text-ink'}`}>
                          {celula.preco_metro === null ? '—' : preco.format(celula.preco_metro)}
                        </span>
                        {/* A diferença ganha cor para saltar aos olhos: âmbar quando está
                            acima do melhor, vermelho a partir de 10% — os argumentos mais fortes.
                            O valor vem negativo, como na planilha: (melhor − preço) / preço. */}
                        <span
                          className={`block text-[10px] font-bold ${
                            celula.nao_cotou
                              ? 'text-muted'
                              : celula.vencedor
                                ? 'text-success'
                                : celula.acima_do_melhor <= -0.1
                                  ? 'text-danger'
                                  : 'text-warning'
                          }`}
                        >
                          {celula.nao_cotou
                            ? 'não cotou'
                            : celula.vencedor
                              ? 'melhor'
                              : percentual(celula.acima_do_melhor)}
                        </span>
                      </td>
                    ))}
                    {linha.precos.map((celula, indice) => (
                      <td
                        key={`pacote-${celula.fornecedor_id}`}
                        className={`border-r border-border px-sm py-xs text-right tabular-nums ${
                          indice === 0 ? 'border-l-2 border-l-ink-soft/30' : ''
                        } ${celula.vencedor ? 'bg-success/10' : ''} ${celula.excluido ? 'opacity-40' : ''}`}
                      >
                        <span className={`block ${celula.vencedor ? 'font-bold text-success' : 'text-ink'}`}>
                          {celula.preco_efetivo === null ? '—' : reais.format(celula.preco_efetivo * linha.quantidade_m)}
                        </span>
                        {/* Como na planilha: sem cotação, a linha entra no pacote pelo menor preço. */}
                        {celula.nao_cotou && <span className="block text-[10px] text-muted">pelo menor preço</span>}
                      </td>
                    ))}
                    <td className="px-sm py-xs text-right font-bold tabular-nums text-accent">
                      {linha.melhor_preco === null ? '—' : reais.format(linha.melhor_preco * linha.quantidade_m)}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="z-20 xl:sticky xl:bottom-0">
                <tr className="text-[12px]">
                  <td className={`${RODAPE_FIXO} sticky left-0 z-10 font-bold text-ink`}>Total da cotação</td>
                  <td className={`${RODAPE_FIXO} text-right tabular-nums text-ink`}>
                    {inteiro.format(equalizacao.quantidade_total_m)} m
                  </td>
                  {equalizacao.fornecedores.map((f) => (
                    <td key={`total-preco-${f.id}`} className={RODAPE_FIXO} />
                  ))}
                  {equalizacao.fornecedores.map((f) => {
                    const vence = !f.excluido && f.pacote === menorPacote;

                    return (
                      <td key={`total-pacote-${f.id}`} className={`${RODAPE_FIXO} text-right tabular-nums ${f.excluido ? 'opacity-50' : ''}`}>
                        <span className={`block font-bold ${vence ? 'text-success' : 'text-ink'}`}>{reais.format(f.pacote)}</span>
                        <span className="block text-[10px] text-muted">
                          {f.excluido ? 'fora da análise' : `${percentual(-f.acima_do_melhor)} acima do melhor`}
                        </span>
                      </td>
                    );
                  })}
                  <td className={`${RODAPE_FIXO} text-right`}>
                    <span className="block font-bold tabular-nums text-accent">{reais.format(equalizacao.melhor_pacote)}</span>
                    <span className="block text-[10px] text-muted">menor preço de cada item</span>
                  </td>
                </tr>
              </tfoot>
            </table>
          )}
        </div>
      </div>

      <Dialog open={confirmandoSeparacao} onOpenChange={(aberto) => !separar.isPending && setConfirmandoSeparacao(aberto)}>
        <DialogContent className="max-w-[460px] p-lg">
          <DialogTitle>
            Separar {marcados.length === 1 ? '1 item' : `${marcados.length} itens`} em nova cotação?
          </DialogTitle>
          <DialogDescription className="mt-xs">
            Eles saem desta cotação e vão para uma cotação nova, com os preços da última rodada. Lá seguem a negociação e o
            fechamento próprios — por exemplo, para comprar de outro fornecedor.
          </DialogDescription>
          {erroDaSeparacao && <p className="mt-sm text-[13px] font-bold text-danger">{erroDaSeparacao}</p>}
          <div className="mt-lg flex justify-end gap-sm">
            <button
              type="button"
              onClick={() => setConfirmandoSeparacao(false)}
              disabled={separar.isPending}
              className="rounded-md border border-border px-md py-sm text-[13px] font-bold text-ink hover:bg-surface-subtle"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={() => separar.mutate()}
              disabled={separar.isPending}
              className="inline-flex items-center gap-xs rounded-md bg-accent px-md py-sm text-[13px] font-bold text-white disabled:opacity-50"
            >
              <Scissors size={14} aria-hidden="true" />
              {separar.isPending ? 'Separando…' : 'Separar'}
            </button>
          </div>
        </DialogContent>
      </Dialog>

      <p className="mt-sm text-[12px] text-muted">
        Tirar um fornecedor da análise não apaga preço nenhum: ele volta a disputar assim que for
        reincluído. Item que o fornecedor não cotou entra pelo menor preço da linha e nunca vence.
      </p>
    </div>
  );
}

/**
 * Separar leva a outra cotação na mesma rota; sem remontar, a rodada, os
 * fornecedores tirados e os itens marcados da cotação anterior ficariam na tela.
 */
export default function EqualizacaoPage() {
  const { cotacaoId } = useParams();

  return <Equalizacao key={cotacaoId} />;
}
